/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';

const Home = lazy(() => import('./pages/Home'));
const MeeshoTool = lazy(() => import('./pages/MeeshoTool'));
const FlipkartTool = lazy(() => import('./pages/FlipkartTool'));
const AmazonTool = lazy(() => import('./pages/AmazonTool'));
const ManualCropper = lazy(() => import('./pages/ManualCropper'));

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <div className="min-h-screen bg-[#FAFAFA] text-[#333] font-sans">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh] text-gray-400 font-bold">Loading Tools...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/meesho-cropper" element={<MeeshoTool />} />
              <Route path="/flipkart-cropper" element={<FlipkartTool />} />
              <Route path="/amazon-cropper" element={<AmazonTool />} />
              <Route path="/manual-cropper" element={<ManualCropper />} />
            </Routes>
          </Suspense>
        </main>
        <footer className="pt-24 pb-12 border-t border-gray-100 bg-white relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-indigo-200 to-transparent" />
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
              <div className="space-y-6 text-center md:text-left col-span-1 lg:col-span-1">
                <img 
                  src="https://webtoolsdeck.com/wp-content/uploads/2025/12/Untitled-design-2-scaled-e1765227637238.png" 
                  alt="WebTools Deck Logo" 
                  className="h-12 w-auto mx-auto md:mx-0"
                  width="180"
                  height="48"
                  referrerPolicy="no-referrer"
                />
                <p className="text-gray-700 text-sm font-bold leading-relaxed max-w-xs mx-auto md:mx-0">
                  WebTools Deck is the #1 suite for ecommerce sellers in India. We build high-precision, privacy-first PDF utilities to streamline your warehouse logistics.
                </p>
                <div className="flex items-center justify-center md:justify-start gap-4">
                   <button aria-label="Visit us on X" className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:border-indigo-100 transition-all cursor-pointer">
                      <span className="text-xl">𝕏</span>
                   </button>
                   <button aria-label="Follow us on Instagram" className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:border-indigo-100 transition-all cursor-pointer">
                      <span className="text-xl">📸</span>
                   </button>
                   <button aria-label="Watch us on YouTube" className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:border-indigo-100 transition-all cursor-pointer">
                      <span className="text-xl">📺</span>
                   </button>
                </div>
              </div>

              <div className="space-y-6">
                <h4 className="text-gray-900 font-black uppercase tracking-widest text-xs">Marketplace Studios</h4>
                <ul className="space-y-4">
                  <li><Link to="/meesho-cropper" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Meesho Smart Cropper</Link></li>
                  <li><Link to="/flipkart-cropper" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Flipkart 4x6 Resizer</Link></li>
                  <li><Link to="/amazon-cropper" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Amazon Label Studio</Link></li>
                  <li><Link to="/manual-cropper" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">PDF Cropper Studio</Link></li>
                </ul>
              </div>

              <div className="space-y-6">
                <h4 className="text-gray-900 font-black uppercase tracking-widest text-xs">Resources</h4>
                <ul className="space-y-4">
                  <li><a href="#" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Usage Guide</a></li>
                  <li><a href="#" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Privacy Policy</a></li>
                  <li><a href="#" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Terms of Service</a></li>
                  <li><a href="#" className="text-sm font-bold text-gray-700 hover:text-indigo-600 transition-colors">Contact Support</a></li>
                </ul>
              </div>

              <div className="space-y-6">
                <h4 className="text-gray-900 font-black uppercase tracking-widest text-xs">Newsletter</h4>
                <p className="text-xs text-gray-700 font-bold leading-relaxed">Get the latest seller tips and tool updates delivered to your inbox.</p>
                <div className="flex gap-2">
                  <input type="email" placeholder="Email address" className="bg-gray-100 border-2 border-gray-200 rounded-xl px-4 py-2 text-sm font-bold w-full focus:outline-none focus:border-indigo-100" />
                  <button aria-label="Subscribe to newsletter" className="bg-indigo-600 text-white p-2 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100">
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="pt-12 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-center md:text-left space-y-1">
                <p className="text-gray-900 font-black text-sm">
                  &copy; {new Date().getFullYear()} WebTools Deck. All Rights Reserved.
                </p>
                <p className="text-gray-500 text-[10px] font-black uppercase tracking-[0.2em]">
                  Handcrafted for Bharat Entrepreneurs
                </p>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Systems Operational</span>
                </div>
                <div className="bg-gray-900 text-white px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-xl shadow-black/10">
                   India's #1 Label suite
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Router>
  );
}
